import { createContext, useContext, useEffect, ReactNode } from "react";
import { useAuthStore } from "@/stores/authStore";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "@/constants/routes";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, user, accessToken, logout } =
    useAuthStore();

  const checkAuth = async () => {
    if (accessToken) {
      try {
        const isValid = await useAuthStore.getState().checkTokenValidity();

        if (!isValid) {
          await logout();
          navigate(ROUTES.LOGIN);
        } else {
          console.log("✅ Token is valid, staying logged in");
        }
      } catch (error) {
        await logout();
        navigate(ROUTES.LOGIN);
      }
    } else {
      console.log("ℹ️ No access token found");
    }
  };

  useEffect(() => {
    // Kiểm tra auth khi component mount - TẠM THỜI DISABLE
    // checkAuth();
    console.log("🚀 AuthContext mounted, isAuthenticated:", isAuthenticated);
  }, []);

  useEffect(() => {
    // Redirect logic dựa trên auth status
    const isLoginPage = location.pathname === ROUTES.LOGIN;

    if (!isLoading) {
      if (isAuthenticated && isLoginPage) {
        navigate(ROUTES.DASHBOARD);
      } else if (!isAuthenticated && !isLoginPage) {
        navigate(ROUTES.LOGIN);
      } else {
        console.log("✅ No redirect needed");
      }
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
